import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Field, inputClass } from '../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { errorMessage } from '../api/errors';

const GoogleButton = (): JSX.Element => {
  const { startGoogleLogin } = useAuth();
  return (
    <Button type="button" variant="secondary" className="w-full" onClick={startGoogleLogin}>
      Continue with Google
    </Button>
  );
};

const SynergyButton = (): JSX.Element => {
  const { startSynergyLogin } = useAuth();
  return (
    <Button type="button" variant="secondary" className="w-full" onClick={startSynergyLogin}>
      Continue with Synergy
    </Button>
  );
};

const Divider = (): JSX.Element => (
  <div className="flex items-center gap-3 text-xs text-ink-400">
    <span className="h-px flex-1 bg-ink-700" />
    or
    <span className="h-px flex-1 bg-ink-700" />
  </div>
);

const useAfterAuthRedirect = (): (() => void) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  return () => navigate(from ?? '/', { replace: true });
};

export const LoginPage = (): JSX.Element => {
  const { login } = useAuth();
  const done = useAfterAuthRedirect();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login({ email, password });
      done();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm">
      <h1 className="mb-6 text-xl font-semibold">Sign in to Streamr</h1>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={inputClass}
          />
        </Field>
        {error !== null && (
          <p className="text-sm text-red-300" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" disabled={pending}>
          {pending ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>

      <div className="my-4">
        <Divider />
      </div>
      <div className="flex flex-col gap-2">
        <SynergyButton />
        <GoogleButton />
      </div>

      <p className="mt-6 text-sm text-ink-300">
        No account?{' '}
        <Link to="/signup" className="text-brand-400 hover:underline">
          Create one
        </Link>
      </p>
    </div>
  );
};

export const SignupPage = (): JSX.Element => {
  const { signup } = useAuth();
  const done = useAfterAuthRedirect();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  /*
   * Mirrors handleSchema in @streamr/shared. Validating here as well as on the
   * server is not duplication for its own sake -- it is the difference between
   * telling someone about a bad handle as they type it and telling them after a
   * round trip that also rejected their password.
   */
  const handleError =
    handle !== '' && !/^[a-z0-9_]{3,30}$/.test(handle)
      ? 'Lowercase letters, digits and underscores; 3 to 30 characters.'
      : undefined;
  const passwordError =
    password !== '' && password.length < 10 ? 'At least 10 characters.' : undefined;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (handleError !== undefined || passwordError !== undefined) return;
    setPending(true);
    setError(null);
    try {
      await signup({ email, password, handle, displayName });
      done();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm">
      <h1 className="mb-6 text-xl font-semibold">Create your channel</h1>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <Field label="Display name" htmlFor="displayName">
          <input
            id="displayName"
            required
            maxLength={60}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Handle" htmlFor="handle" error={handleError} hint="This is your channel's public address.">
          <input
            id="handle"
            required
            value={handle}
            onChange={(event) => setHandle(event.target.value.toLowerCase())}
            className={inputClass}
          />
        </Field>
        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password" htmlFor="password" error={passwordError}>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={inputClass}
          />
        </Field>
        {error !== null && (
          <p className="text-sm text-red-300" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" disabled={pending}>
          {pending ? 'Creating...' : 'Create account'}
        </Button>
      </form>

      <div className="my-4">
        <Divider />
      </div>
      <div className="flex flex-col gap-2">
        <SynergyButton />
        <GoogleButton />
      </div>

      <p className="mt-6 text-sm text-ink-300">
        Already have an account?{' '}
        <Link to="/login" className="text-brand-400 hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
};
